import { CellContext } from '@tanstack/react-table';
import { TestQuestionRow } from './StepQuestionsResultsTable';
import styled from 'styled-components';
import {
  TooltipContent,
  TooltipDetail,
  TooltipTitle,
} from '../../Progress/Tooltip';
import { SmallPortalTooltip } from '../../../Tooltip';

const SkillsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  flex-wrap: nowrap;
  overflow-x: auto;
  white-space: nowrap;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const SkillPill = styled.div<{ $avgScore?: number }>`
  display: flex;
  min-width: fit-content;
  flex-wrap: nowrap;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 8px;
  justify-content: space-between;
  padding: 4px 12px;
  background-color: ${({ theme, $avgScore }) => {
    if ($avgScore === undefined) return theme.colors.bgDefaultSecondary;
    if ($avgScore >= 8) return theme.colors.primaryBg;
    if ($avgScore >= 6) return theme.colors.secondaryBg;
    if ($avgScore >= 4) return theme.colors.secondaryBg;
    return theme.colors.bgDanger;
  }};
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const SkillName = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textRegular};
`;

const SkillScore = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textActive};
`;

const TooltipObjectiveRow = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.bgActive};

  &:first-child {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
`;

const ObjectiveStatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background-color: ${({ theme, $status }) =>
    $status === 'exceeded'
      ? theme.colors.primaryBg
      : $status === 'met'
      ? theme.colors.primaryBg
      : $status === 'partial'
      ? theme.colors.secondaryBg
      : $status === 'not_met'
      ? theme.colors.bgDanger
      : theme.colors.bgDefaultSecondary};
`;

type ObjectiveWithEvaluation = {
  id: string;
  description?: string;
  status?: string;
  score?: number;
  level?: string;
  evidenceAnalysis?: string;
  decisionRationale?: string;
};

type SkillAggregation = {
  skill: string;
  skillName: string;
  objectives: ObjectiveWithEvaluation[];
  avgScore?: number;
};

export default function QuestionSkillsReviewCell(
  info: CellContext<TestQuestionRow, unknown>
) {
  const sectionId = info.row.original.section.id;
  const stepObjectives = info.row.original.stepObjectives ?? [];
  const scenarioEvaluation = info.row.original.stepReview;
  const dimensions = info.row.original.dimensions ?? [];

  // Filter objectives relevant to this question
  const questionRelevantObjectives = stepObjectives
    .filter((objective) =>
      objective.facets.some((facet) => facet.questions.includes(sectionId))
    )
    .map((objective) => {
      const objectiveEvaluation = scenarioEvaluation?.objectiveEvaluations.find(
        (evaluation) => evaluation.objectiveId === objective.id
      );
      return {
        id: objective.id,
        description: objectiveEvaluation?.objectiveDescription,
        status: objectiveEvaluation?.status,
        score: objectiveEvaluation?.score,
        level: objectiveEvaluation?.comparativeLevel,
        skill: objectiveEvaluation?.skill,
        evidenceAnalysis: objectiveEvaluation?.evidenceAnalysis,
        decisionRationale: objectiveEvaluation?.decisionRationale,
      };
    });

  // Group objectives by skill and calculate average scores
  const skillsMap = new Map<
    string,
    SkillAggregation & { skillLabel: string }
  >();

  questionRelevantObjectives.forEach((objective) => {
    const skill = objective.skill;
    if (!skill) return;

    if (!skillsMap.has(skill)) {
      const dimension = dimensions.find((d) => d.id === skill);
      skillsMap.set(skill, {
        skill,
        skillName: dimension?.name ?? skill,
        skillLabel: dimension?.label ?? skill,
        objectives: [],
      });
    }

    const skillObj = skillsMap.get(skill);
    if (skillObj) {
      skillObj.objectives.push({
        id: objective.id,
        description: objective.description,
        status: objective.status,
        score: objective.score,
        level: objective.level,
        evidenceAnalysis: objective.evidenceAnalysis,
        decisionRationale: objective.decisionRationale,
      });
    }
  });

  // Calculate average scores for each skill
  const skills: (SkillAggregation & { skillLabel: string })[] = Array.from(
    skillsMap.values()
  ).map((skillAgg) => {
    const scoresWithValues = skillAgg.objectives
      .map((obj) => obj.score)
      .filter((score): score is number => score !== undefined);

    const avgScore =
      scoresWithValues.length > 0
        ? scoresWithValues.reduce((sum, score) => sum + score, 0) /
          scoresWithValues.length
        : undefined;

    return {
      ...skillAgg,
      avgScore,
    };
  });

  const shouldShowResults = scenarioEvaluation !== null;
  const questionId = info.row.original.id;

  return (
    <SkillsWrapper>
      {skills.map((skill) => (
        <div key={skill.skill}>
          <SkillPill
            data-tooltip-id={`skill-${skill.skill}-${questionId}`}
            $avgScore={skill.avgScore}
          >
            <SkillName>
              {skill.skillLabel} ({skill.objectives.length})
            </SkillName>
            {shouldShowResults && skill.avgScore !== undefined && (
              <SkillScore>{skill.avgScore.toFixed(1)}/10</SkillScore>
            )}
          </SkillPill>
          <SmallPortalTooltip
            anchorSelect={`[data-tooltip-id="skill-${skill.skill}-${questionId}"]`}
          >
            <TooltipContent>
              <TooltipTitle>{skill.skillName}</TooltipTitle>
              {shouldShowResults && skill.avgScore !== undefined && (
                <>
                  <TooltipDetail style={{ marginTop: '8px' }}>
                    Average Score:{' '}
                    <strong>{skill.avgScore.toFixed(1)}/10</strong>
                  </TooltipDetail>
                  <TooltipDetail style={{ marginTop: '8px', opacity: 0.8 }}>
                    Based on {skill.objectives.length}{' '}
                    {skill.objectives.length === 1 ? 'objective' : 'objectives'}
                  </TooltipDetail>
                </>
              )}

              {skill.objectives.map((objective) => (
                <TooltipObjectiveRow key={objective.id}>
                  {shouldShowResults && (
                    <>
                      {objective.description && (
                        <TooltipDetail style={{ fontSize: '12px' }}>
                          {objective.description}
                        </TooltipDetail>
                      )}
                      {objective.status && (
                        <div style={{ marginTop: '4px' }}>
                          <ObjectiveStatusBadge $status={objective.status}>
                            {objective.status === 'exceeded'
                              ? 'Exceeded'
                              : objective.status === 'met'
                              ? 'Met'
                              : objective.status === 'partial'
                              ? 'Partial'
                              : objective.status === 'not_met'
                              ? 'Not Met'
                              : 'Not Tested'}
                          </ObjectiveStatusBadge>
                        </div>
                      )}
                      {objective.score !== undefined && (
                        <>
                          <TooltipTitle style={{ marginTop: '8px' }}>
                            Score
                          </TooltipTitle>
                          <TooltipDetail>{objective.score}/10</TooltipDetail>
                        </>
                      )}
                    </>
                  )}
                </TooltipObjectiveRow>
              ))}
            </TooltipContent>
          </SmallPortalTooltip>
        </div>
      ))}
      {skills.length === 0 && '-'}
    </SkillsWrapper>
  );
}
